import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { trackEvent } from "utils/tracking";
import { STRATEGY_DEPLOY_STATUS } from "utils/constants";
import { deployStrategyVersion } from "features/Strategy/service";
import { selectIsBacktestComplete } from "features/Strategy/slice/selector";
import { useStrategyVersions } from "./useStrategyVersions";

const NO_PENDING_CHANGES_TOOLTIP =
  "No changes to deploy. Save an edit and run a backtest first.";
const NEEDS_BACKTEST_TOOLTIP =
  "Run a backtest of your saved changes before deploying.";
const CHECKING_TOOLTIP = "Checking for saved changes...";
const CHECK_FAILED_TOOLTIP =
  "Could not check for saved changes. Refresh the page and try again.";
const ALREADY_DEPLOYED_TOOLTIP = "These changes are already deployed.";
const DEPLOYED_TOAST =
  "Your changes are deployed and will take effect at the next trading cycle";
const MOVED_ON_TOAST =
  "Your saved changes have moved on since you opened this dialog. Review them and deploy again.";

/**
 * Single source of truth for the Deploy CTA across all three builders.
 *
 * `deployMode` is the fork: "connect" keeps the create flow (open the broker
 * modal), "changes" routes an edit of an already-trading strategy through the
 * version deploy endpoint.
 */
export const useDeployVersion = ({
  strategyId,
  canEditCode,
  deployStatus,
  strategyName,
  builderType,
  onDeployed,
} = {}) => {
  const dispatch = useDispatch();
  const isBacktestComplete = useSelector(selectIsBacktestComplete);

  const [isDeploying, setIsDeploying] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // The version the modal described, and the version the POST promoted. The
  // deploy endpoint takes no id and always promotes the newest row, so both are
  // needed to keep the button honest about what it would send.
  const [confirmingVersionId, setConfirmingVersionId] = useState(null);
  const [deployedVersionId, setDeployedVersionId] = useState(null);

  const {
    isLoading: isLoadingVersions,
    isError: isVersionsError,
    deployedVersion,
    pendingVersion,
    refetch: refetchVersions,
  } = useStrategyVersions(strategyId, canEditCode);

  const isVersionsEnabled = Boolean(strategyId) && Boolean(canEditCode);
  const isLive = deployStatus === STRATEGY_DEPLOY_STATUS.DEPLOYED;
  const hasPendingChanges = pendingVersion != null;
  // A refetch that fails keeps the pre-deploy payload, so the id we promoted is
  // the only reliable sign that this pending row is already live.
  const isPendingAlreadyDeployed =
    hasPendingChanges &&
    deployedVersionId != null &&
    pendingVersion.id === deployedVersionId;
  // `deployable` is the backend's backtest gate. Never recompute it here.
  const canDeployChanges =
    isLive &&
    !isLoadingVersions &&
    !isVersionsError &&
    hasPendingChanges &&
    Boolean(pendingVersion.deployable) &&
    !isPendingAlreadyDeployed;
  const deployMode = isLive ? "changes" : "connect";
  const deployLabel = isLive ? "Deploy Changes" : "Deploy";

  const deployChangesTooltip = useMemo(() => {
    if (!isLive || canDeployChanges) return "";
    if (isLoadingVersions) return CHECKING_TOOLTIP;
    if (isVersionsError) return CHECK_FAILED_TOOLTIP;
    if (isPendingAlreadyDeployed) return ALREADY_DEPLOYED_TOOLTIP;
    if (!hasPendingChanges) return NO_PENDING_CHANGES_TOOLTIP;
    return NEEDS_BACKTEST_TOOLTIP;
  }, [
    isLive,
    canDeployChanges,
    isLoadingVersions,
    isVersionsError,
    isPendingAlreadyDeployed,
    hasPendingChanges,
  ]);

  // A finished backtest is what turns `deployable` true, and BacktestingTab
  // flips this redux flag when the log stream ends. The flag is global and
  // survives navigation between strategies, so react to the transition only,
  // and only once this strategy's query is allowed to run.
  const wasBacktestComplete = useRef(isBacktestComplete);
  useEffect(() => {
    if (!isVersionsEnabled) return;
    const previous = wasBacktestComplete.current;
    wasBacktestComplete.current = isBacktestComplete;
    if (isBacktestComplete && !previous) refetchVersions();
  }, [isBacktestComplete, isVersionsEnabled, refetchVersions]);

  const openConfirmModal = useCallback(() => {
    setConfirmingVersionId(pendingVersion?.id ?? null);
    setShowConfirmModal(true);
  }, [pendingVersion]);

  const closeConfirmModal = useCallback(() => {
    setConfirmingVersionId(null);
    setShowConfirmModal(false);
  }, []);

  // ConfirmationModal keeps its confirm button clickable while it shows the
  // loader, so the in-flight guard cannot wait for a re-render.
  const isDeployingRef = useRef(false);

  const confirmDeploy = useCallback(async () => {
    if (isDeployingRef.current) return;
    isDeployingRef.current = true;
    setIsDeploying(true);

    const trackDeployFailed = (version, message) =>
      trackEvent({
        name: "deploy_strategy_changes_failed",
        label: "Strategy",
        strategy_id: strategyId,
        strategy_name: strategyName,
        builder_type: builderType,
        version_id: version?.id,
        commit_sha: version?.commit_sha,
        error: message,
      });

    try {
      // The POST carries no version id, so the server promotes whatever the
      // newest row is. Re-read it here: a save from another tab must not ride
      // in on a confirmation the user gave for different code.
      const result = await refetchVersions();

      if (result?.isError) {
        const message = result.error
          ? parseErrorMessage(result.error)
          : CHECK_FAILED_TOOLTIP;
        dispatch(toast({ type: "error", body: message }));
        trackDeployFailed(pendingVersion, message);
        return;
      }

      const latest = result?.data?.pending ?? null;
      if (!latest || latest.id !== confirmingVersionId || !latest.deployable) {
        dispatch(toast({ type: "error", body: MOVED_ON_TOAST }));
        trackDeployFailed(latest, MOVED_ON_TOAST);
        setConfirmingVersionId(null);
        setShowConfirmModal(false);
        return;
      }

      await deployStrategyVersion(strategyId);
      setDeployedVersionId(latest.id);
      dispatch(toast({ body: DEPLOYED_TOAST }));
      trackEvent({
        name: "deploy_strategy_changes_confirmed",
        label: "Strategy",
        strategy_id: strategyId,
        strategy_name: strategyName,
        builder_type: builderType,
        version_id: latest.id,
        commit_sha: latest.commit_sha,
      });
      await refetchVersions();
      if (onDeployed) onDeployed();
      setConfirmingVersionId(null);
      setShowConfirmModal(false);
    } catch (error) {
      // The backend's 400 bodies are already user-facing. Leave the modal open
      // so the user can retry once they have fixed what it names.
      const message = parseErrorMessage(error);
      dispatch(toast({ type: "error", body: message }));
      trackDeployFailed(pendingVersion, message);
    } finally {
      isDeployingRef.current = false;
      setIsDeploying(false);
    }
  }, [
    strategyId,
    strategyName,
    builderType,
    confirmingVersionId,
    pendingVersion,
    dispatch,
    refetchVersions,
    onDeployed,
  ]);

  return {
    isLive,
    deployedVersion,
    pendingVersion,
    hasPendingChanges,
    canDeployChanges,
    deployMode,
    deployLabel,
    deployChangesTooltip,
    isDeploying,
    showConfirmModal,
    openConfirmModal,
    closeConfirmModal,
    confirmDeploy,
    refetchVersions,
  };
};
